import type { AnyExtension, Editor } from '@tiptap/core'
import { Highlight } from '@tiptap/extension-highlight'
import { Link } from '@tiptap/extension-link'
import { Subscript } from '@tiptap/extension-subscript'
import { Superscript } from '@tiptap/extension-superscript'
import { Typography } from '@tiptap/extension-typography'
import { Underline } from '@tiptap/extension-underline'
import { Placeholder } from '@tiptap/extensions'
import type { UseEditorOptions } from '@tiptap/react'
import { useEditor } from '@tiptap/react'
import { StarterKit } from '@tiptap/starter-kit'
import { twMerge } from 'tailwind-merge'

import {
  COMMENT_TYPOGRAPHY_CLASSNAME,
  validateAndConvertContent,
} from '@/components/pursuits/v2/dashboard/comments/lib'

import { Mention, Suggestions, useSuggestions } from '@/extensions/Mention'

interface UseCommentEditorProps extends UseEditorOptions {
  className?: string
  placeholder?: string
}

interface GetBaseExtensionsProps {
  extensions?: AnyExtension[]
  className?: string
  placeholder?: string
  mentionSuggestions?: Suggestions
}

export const getBaseExtensions = ({
  extensions,
  className,
  placeholder,
  mentionSuggestions,
}: GetBaseExtensionsProps = {}) => {
  return [
    StarterKit.configure({
      // Disable nodes we don't need for comments
      heading: false,
      horizontalRule: false,
      link: false,
      underline: false,
      blockquote: {
        HTMLAttributes: {
          class:
            'border-l-4 border-primary-300 dark:border-primary-600 pl-4 italic',
        },
      },
      codeBlock: {
        HTMLAttributes: {
          class:
            'bg-primary-100 dark:bg-primary-800 rounded p-3 font-mono text-sm overflow-x-auto',
        },
      },
      code: {
        HTMLAttributes: {
          class:
            'bg-primary-100 dark:bg-primary-800 rounded px-1.5 py-0.5 font-mono text-sm',
        },
      },
    }),
    Underline,
    Highlight.configure({
      HTMLAttributes: {
        class: 'bg-yellow-200 dark:bg-yellow-800 px-0.5 rounded',
      },
    }),
    Superscript,
    Subscript,
    Typography,
    Mention.configure({
      HTMLAttributes: {
        class:
          'bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 px-1.5 py-0.5 rounded font-medium cursor-default',
      },
      renderText: ({ options, node }) => {
        return `${options.suggestion.char}${node.attrs.label ?? node.attrs.id}`
      },
      suggestion: mentionSuggestions,
    }),
    Link.configure({
      openOnClick: true,
      HTMLAttributes: {
        class:
          'text-blue-600 dark:text-blue-400 no-underline hover:underline underline-offset-2  ',
      },
    }),
    Placeholder.configure({
      showOnlyWhenEditable: false,
      showOnlyCurrent: true,
      emptyNodeClass: twMerge(
        'cursor-text before:text-primary-300 dark:before:text-primary-500 font-normal',
        'cursor-text first:before:content-[attr(data-placeholder)] before:text-primary-400 dark:before:text-primary-500 before:text-sm font-normal before:h-0 before:float-left before:pointer-events-none',
        className,
      ),
      placeholder: placeholder ?? 'Write a comment...',
    }),
    ...(extensions ?? []),
  ]
}

export const useCommentEditor = ({
  content,
  extensions,
  className,
  editorProps,
  immediatelyRender = false,
  placeholder,
  ...options
}: UseCommentEditorProps): Editor | undefined => {
  const mentionSuggestions = useSuggestions({
    suggestion: { type: 'user' },
  })

  return useEditor({
    extensions: getBaseExtensions({
      extensions: extensions ?? [],
      className,
      placeholder,
      mentionSuggestions,
    }),
    content:
      typeof content === 'string'
        ? validateAndConvertContent(content)
        : content,
    editorProps: {
      attributes: {
        class: twMerge(
          'rich-text-editor-content my-0 max-w-full',
          COMMENT_TYPOGRAPHY_CLASSNAME,
          'leading-[20px] focus:outline-none',
          className ?? '',
        ),
      },
      ...editorProps,
    },
    immediatelyRender,
    shouldRerenderOnTransaction: false,
    ...options,
  })
}
